﻿import { Switch, Text } from '@mantine/core';
import { Icon } from '@uag/react-core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { GetMainRepresentationByArticleIdLayerLoadMode as LayerLoadMode, LayerModelLayerType } from 'api/models';
import { useGetMainRepresentationByArticleId } from 'api/queries/articles/articles';
import { getLayerColor } from 'app/modules/wallforming/components/scenes/objects/articles/utils/layerColorMap';
import {
    buildPartLayerVisibilityKey,
    buildPrefabTree,
    getPrefabLayerGeometryCounts,
    getPrefabLayerTypes,
    PrefabTreeNode
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/prefabGeometryUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const GRID = 'grid grid-cols-[16px_1fr_28px_32px] items-center gap-2 px-2 py-0.5';

interface RowProps {
    color: string;
    label: string;
    count?: number;
    checked: boolean;
    onToggle: (v: boolean) => void;
    separator?: boolean;
}

const Row = ({ color, label, count, checked, onToggle, separator }: RowProps) => (
    <div className={`${GRID}${separator ? ' border-b border-border-light' : ''}`}> 
        <div
            className="w-2 h-2 rounded-sm shrink-0 border border-border-light"
            style={{ backgroundColor: color }}
        />
        <span className="min-w-0 text-xs font-mono truncate leading-tight text-text-default" title={label}>
            {label}
        </span>
        <span className="text-xs text-text-placeholder text-right tabular-nums">
            {count !== undefined ? count : ''}
        </span>
        <span className="flex items-center justify-center">
            <Switch
                checked={checked}
                className="text-xs text-text-placeholder text-right tabular-nums"
                label={'\u200b'}
                onChange={(e) => onToggle(e.currentTarget.checked)}
            />
        </span>
    </div>
);

interface PrefabLayerTreeContentProps {
    articleId: string;
}

interface PartSectionProps {
    node: PrefabTreeNode;
    depth: number;
    meshInfosByPart: Map<string, Array<{ id: string; name: string; triangles: number }>>;
    isLayerVisible: (partPath: string, layerType: LayerModelLayerType) => boolean;
    isMeshVisible: (meshId: string) => boolean;
    setPartLayerVisibility: (partPath: string, layerType: LayerModelLayerType, visible: boolean) => void;
    setMeshVisibility: (meshId: string, visible: boolean) => void;
}

const PartSection = ({
    node,
    depth,
    meshInfosByPart,
    isLayerVisible,
    isMeshVisible,
    setPartLayerVisibility,
    setMeshVisibility,
}: PartSectionProps) => {
    const layerCounts = useMemo(() => {
        const counts = new Map<LayerModelLayerType, number>();
        node.layers.forEach((layer) => {
            counts.set(layer.layerType, (counts.get(layer.layerType) ?? 0) + layer.geometries.length);
        });
        return counts;
    }, [node.layers]);

    const partMeshes = meshInfosByPart.get(node.name) ?? [];
    const indent = depth * 12;

    return (
        <>
            <div className="flex items-center gap-2 px-2 py-1 border-b border-border-light bg-background-gray-ultra-light/60">
                <span className="text-xs text-text-placeholder" style={{ marginLeft: `${indent}px` }}>▾</span>
                <span className="text-xs font-semibold text-text-default truncate" title={node.name}>{node.name}</span>
                <span className="ml-auto text-[10px] text-text-placeholder tabular-nums">
                    {partMeshes.length} meshes · {node.layers.length} layers
                </span>
            </div>

            {partMeshes.map((info) => (
                <div
                    key={info.id}
                    className="flex items-center gap-2 pr-2 py-0.5 hover:bg-background-gray-ultra-light transition-colors"
                    style={{ paddingLeft: `${20 + indent}px` }}
                >
                    <span className="flex-1 min-w-0 text-xs font-mono truncate leading-tight text-text-placeholder" title={info.name}>
                        {info.name}
                    </span>
                    <span className="flex w-5 items-center justify-center text-xs text-text-placeholder tabular-nums" title="triangles">
                        {info.triangles}
                        <Icon icon={'change_history'} size="xs" style={{ color: 'inherit' }} />
                    </span>
                    <span className="flex items-center justify-center">
                        <Switch
                            checked={isMeshVisible(info.id)}
                            className="text-xs text-text-placeholder text-right tabular-nums"
                            label={'\u200b'}
                            onChange={(e) => setMeshVisibility(info.id, e.currentTarget.checked)}
                        />
                    </span>
                </div>
            ))}

            {Array.from(layerCounts.entries()).map(([lt, count]) => (
                <div key={`${node.id}__${lt}`} style={{ marginLeft: `${12 + indent}px` }}>
                    <Row
                        checked={isLayerVisible(node.id, lt)}
                        color={getLayerColor(lt)}
                        count={count}
                        label={lt}
                        onToggle={(v) => setPartLayerVisibility(node.id, lt, v)}
                    />
                </div>
            ))}

            {node.children.map((child) => (
                <PartSection
                    key={child.id}
                    depth={depth + 1}
                    isLayerVisible={isLayerVisible}
                    isMeshVisible={isMeshVisible}
                    meshInfosByPart={meshInfosByPart}
                    node={child}
                    setMeshVisibility={setMeshVisibility}
                    setPartLayerVisibility={setPartLayerVisibility}
                />
            ))}
        </>
    );
};

const PrefabLayerTreeContent = ({ articleId }: PrefabLayerTreeContentProps) => {
    const { t } = useTranslation();

    const { data, isLoading } = useGetMainRepresentationByArticleId(
        articleId,
        { layerLoadMode: LayerLoadMode.All, includePrefabComponents: false },
        {
            query: {
                queryKey: ['representation', articleId, LayerLoadMode.All],
                staleTime: 24 * 60 * 60 * 1000,
            },
        }
    );

    const {
        layerVisibility,
        toggleAllLayerVisibility,
        partLayerVisibility,
        setPartLayerVisibility,
        glbVisible,
        setGlbVisible,
        meshVisibility,
        setMeshVisibility,
        toggleAllMeshVisibility,
    } = usePlacementStore(
        useShallow((state) => ({
            layerVisibility: state.layerVisibility,
            toggleAllLayerVisibility: state.toggleAllLayerVisibility,
            partLayerVisibility: state.partLayerVisibility,
            setPartLayerVisibility: state.setPartLayerVisibility,
            glbVisible: state.glbVisible,
            setGlbVisible: state.setGlbVisible,
            meshVisibility: state.meshVisibility,
            setMeshVisibility: state.setMeshVisibility,
            toggleAllMeshVisibility: state.toggleAllMeshVisibility,
        }))
    );

    const prefab = data?.data;
    const layerTypes = useMemo(() => (prefab ? getPrefabLayerTypes(prefab) : []), [prefab]);
    const geoCounts = useMemo(() => (prefab ? getPrefabLayerGeometryCounts(prefab) : new Map()), [prefab]);
    const prefabTree = useMemo(() => (prefab ? buildPrefabTree(prefab) : null), [prefab]);

    const meshInfos = usePlacementStore((state) => state.articles[articleId]?.meshInfos ?? []);
    const meshInfosByPart = useMemo(() => {
        const map = new Map<string, Array<{ id: string; name: string; triangles: number }>>();
        meshInfos.forEach((mesh) => {
            const key = mesh.partName ?? prefab?.name ?? 'Representation';
            if (!map.has(key)) {
                map.set(key, []);
            }
            map.get(key)!.push({ id: mesh.id, name: mesh.name, triangles: mesh.triangles });
        });
        return map;
    }, [meshInfos, prefab?.name]);

    const isVisible = (partPath: string, lt: LayerModelLayerType) => {
        const key = buildPartLayerVisibilityKey(partPath, lt);
        if (key in partLayerVisibility) {
            return partLayerVisibility[key] !== false;
        }
        return layerVisibility[lt] !== false;
    };
    const isMeshVisible = (meshId: string) => meshVisibility[meshId] !== false;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center flex-1 p-4">
                <Text c="dimmed" size="xs">{t('labelLoading', 'Loading…')}</Text>
            </div>
        );
    }

    if (!prefab || layerTypes.length === 0) {
        return (
            <div className="flex items-center justify-center flex-1 p-4 text-center">
                <Text c="dimmed" size="xs">No layer data available.</Text>
            </div>
        );
    }

    const allVisible = layerTypes.every((lt) => layerVisibility[lt] !== false);
    const meshIds = meshInfos.map((m) => m.id);
    const allMeshesVisible = meshIds.length === 0 || meshIds.every(isMeshVisible);
    const totalGeos = [...geoCounts.values()].reduce((a, b) => a + b, 0);

    return (
        <div style={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div className="overflow-y-auto overscroll-contain" style={{ flex: '1 1 0', minHeight: 0 }}>
                          
                <div className={`${GRID} border-b border-border-light bg-background-gray-ultra-light sticky top-0 z-10`}>
                    <div />
                    <Text c="dimmed" className="min-w-0" size="xs">
                        {layerTypes.length} layers · {totalGeos} geometries
                    </Text>
                    <span />
                    <span className="flex items-center justify-center">
                        <Switch
                            checked={allVisible}
                            label={'\u200b'}
                            size="sm"
                            title={allVisible ? t('hideAllLayers', 'Hide all layers') : t('showAllLayers', 'Show all layers')}
                            onChange={e => toggleAllLayerVisibility(e.currentTarget.checked, layerTypes)}
                        />
                    </span>
                </div>               
                <Row
                    checked={glbVisible}
                    color="#c4a87e"
                    count={meshInfos.length > 0 ? meshInfos.length : undefined}
                    label="GLB mesh"
                    separator={false}
                    onToggle={setGlbVisible}
                />
                <Row
                    checked={allMeshesVisible}
                    color="#9ca3af"
                    count={meshInfos.length > 0 ? meshInfos.length : undefined}
                    label="All meshes"
                    onToggle={(v) => toggleAllMeshVisibility(v, meshIds)}
                />
                
                <div className="my-2 border-t border-border-light" />

                {prefabTree && (
                    <PartSection
                        depth={0}
                        isLayerVisible={isVisible}
                        isMeshVisible={isMeshVisible}
                        meshInfosByPart={meshInfosByPart}
                        node={prefabTree}
                        setMeshVisibility={setMeshVisibility}
                        setPartLayerVisibility={setPartLayerVisibility}
                    />
                )}
            </div>
        </div>
    );
};

export const PrefabLayerTree = () => {
    const selectedArticleId = usePlacementStore((state) => state.selectedArticleId);

    return (
        <div className="flex flex-col bg-background-default" style={{ height: '100%', overflow: 'hidden' }}>
            <div style={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {selectedArticleId ? (
                    <PrefabLayerTreeContent articleId={selectedArticleId} />
                ) : (
                    <div className="flex items-center justify-center flex-1 p-4 text-center">
                        <Text c="dimmed" size="xs">Select an article to inspect layers.</Text>
                    </div>
                )}
            </div>
        </div>
    );
};